import React, { useCallback, useState } from 'react';
import {
  Typography,
  Box,
  Icon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
  Stack,
  createSvgIcon,
} from '@worthy-npm/worthy-common-ui-components';
import Address from './Address';
import { getShippingMethod, getStepper } from '../../slices/schedulingSlice';
import { SchedulingType } from '../../types/scheduling';
import { useAppSelector } from '../../app/hooks';
import PickupTimeContent from './PickupTimeContent';
import AddressLabel from './AddressLabel';
import ShippingOptionLabel from './ShippingOptionLabel';
import PickupTimeLabel from './PickupTimeLabel';
import MethodSelectionContent from './MethodSelectionContent';

const CheckIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <g transform="translate(6, 6)">
      <path
        d="M11 1L10.3933 1.28312C7.65719 2.55998 5.76347 5.14673 5.37294 8.14077L5 11L4.83751 9.99111C4.50515 7.92743 3.00953 6.24203 1 5.66667V5.66667"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="bevel"
      />
    </g>
  </svg>,
  'CheckIcon',
);

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'inherit',
  borderRadius: theme.shape.borderRadius,
  outline: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '&:hover': {
    outline: `1px solid ${theme.palette.divider}`,
  },
  '&.Mui-expanded': {
    outline: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  '&:before': {
    height: 0,
  },
  '&.Mui-disabled': {
    backgroundColor: 'inherit',
  },
  '& .MuiButtonBase-root.Mui-disabled': {
    color: theme.palette.secondary.main,
  },
}));
const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  color: theme.palette.secondary.main,
  padding: theme.spacing(2),
  margin: 0,
  fontWeight: 500,
  '&.Mui-expanded': {
    color: theme.palette.highlight.main,
  },
  '&.Mui-disabled': {
    color: theme.palette.divider,
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)({});

interface Step {
  key: string;
  label: React.ReactNode;
  content?: React.ReactNode;
  isDisabled?: boolean;
}

export default function ShippingStepper({ prev }: { prev: () => void }) {
  const shippingMethod = useAppSelector(getShippingMethod);
  const stepper = useAppSelector(getStepper);
  const [expanded, setExpanded] = useState<number | false>(1);

  const isDisabled = useCallback(
    (index: number) => {
      if (index === 2 && !stepper.addressSelected) {
        return true;
      }
      return false;
    },
    [stepper.addressSelected],
  );

  const steps: Step[] = [
    {
      key: 'shipping-options',
      label: <ShippingOptionLabel />,
      content: <MethodSelectionContent nextStep={() => setExpanded(1)} />,
    },
    {
      key: 'location-information',
      label: <AddressLabel />,
      content: (
        <Address
          nextStep={() => (shippingMethod === SchedulingType.PICKUP ? setExpanded(2) : setExpanded(false))}
        />
      ),
    },
    ...(shippingMethod === SchedulingType.PICKUP
      ? [
        {
          key: 'pickup-time',
          label: <PickupTimeLabel />,
          content: <PickupTimeContent nextStep={() => setExpanded(false)} />,
        },
      ]
      : []),
  ];

  const handleChange = (idx: number) => {
    if (idx === 0) {
      if (expanded === 0) {
        setExpanded(false);
      } else {
        setExpanded(0);
      }
    }
    if (idx === 1 && stepper.addressSelected) {
      setExpanded(1);
    }
    if (idx === 2 && stepper.pickupTimeSelected) {
      setExpanded(2);
    }
  };

  return (
    <Stack gap={2} width="100%">
      {steps.map((step, idx) => (
        <StyledAccordion
          disableGutters
          key={step.key}
          expanded={expanded === idx}
          onChange={() => handleChange(idx)}
          disabled={isDisabled(idx)}
        >
          <StyledAccordionSummary
            expandIcon={
              expanded === idx ? (
                <Icon.ExpandMoreShineIcon color="inherit" fontSize="inherit" />
              ) : (
                <CheckIcon sx={{ color: '#73A0F7' }} />
              )
            }
          >
            <Typography fontWeight={600}>
              {idx + 1}
              .
              {step.label}
            </Typography>
          </StyledAccordionSummary>
          {step.content && (
            <StyledAccordionDetails>
              <Box>{step.content}</Box>
            </StyledAccordionDetails>
          )}
        </StyledAccordion>
      ))}
    </Stack>
  );
}
